/**
 * Storage helpers for the candidate invite token.
 *
 * The invite token is sent via the magic link URL. We persist it in
 * `sessionStorage` so that:
 *   - browser refresh inside the same tab keeps the candidate signed in
 *   - closing the tab clears the token (no localStorage leakage)
 *   - it never collides with backoffice JWT (`auth_token`) keys
 *
 * The token is intentionally NOT shared across tabs.
 */

const INVITE_TOKEN_KEY = 'onboarding.invite.token';

export const setInviteToken = (token: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(INVITE_TOKEN_KEY, token);
  } catch {
    // sessionStorage may be unavailable in private mode; surface via runtime errors below.
  }
};

export const getInviteToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(INVITE_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const clearInviteToken = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(INVITE_TOKEN_KEY);
  } catch {
    // ignore
  }
};

/** Decoded invite-token claims relevant to the client (kept best-effort). */
export interface InviteTokenClaims {
  email?: string;
  case_id?: number;
  application_id?: string;
  fusion_auth_id?: string | null;
  roles?: string[];
  iat?: number;
  exp?: number;
  iss?: string;
}

/**
 * Best-effort decode of the JWT body so the candidate UI can show their email,
 * preflight expiry, etc. Signature is NOT verified — server-side validation
 * is the source of truth on every API call.
 */
export const decodeInviteToken = (token: string | null | undefined): InviteTokenClaims | null => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
    const decoded = atob(padded + padding);
    return JSON.parse(decoded) as InviteTokenClaims;
  } catch {
    return null;
  }
};

/** True when the JWT `exp` claim has passed. Tolerant if exp is missing. */
export const isInviteTokenExpired = (token: string | null | undefined): boolean => {
  const claims = decodeInviteToken(token);
  if (!claims || typeof claims.exp !== 'number') return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return claims.exp <= nowSec;
};
